/**
 * Pure selection logic for the AI harvester / sales-lead "by industry" picker
 * (the two agent-only pools on the Email Campaigns → AI Prospects tab).
 *
 * The selection is a `Set<string>` of vertical keys. The sentinel
 * {@link ALL_VERTICAL} means "the whole pool". The two are **mutually
 * exclusive**: picking an individual industry drops "all", and checking "all"
 * clears every individual pick. An **empty set means nothing is picked** — the
 * "Add" button is disabled — which is DISTINCT from "all".
 *
 * This distinction is the fix for the 2026-07-04 bug: the old code treated an
 * empty set as "all", so (a) unchecking "All industries" produced an empty set
 * that immediately read back as checked ("can't unselect all"), and (b) every
 * industry row rendered checked-by-default via the all-overlay, so selecting a
 * single vertical produced no visible change. Keeping "all" as an explicit
 * sentinel — never inferred from emptiness — makes both states toggle cleanly.
 */
export const ALL_VERTICAL = "__all__"

/** True when the selection is the whole pool ("All industries"). */
export function isAllVerticals(selected: Set<string>): boolean {
  return selected.has(ALL_VERTICAL)
}

/** True when a specific industry row should render checked. Only its OWN
 *  membership counts — "All industries" no longer overlays every row. */
export function isVerticalChecked(selected: Set<string>, vertical: string): boolean {
  return selected.has(vertical)
}

/**
 * Toggle one row of the picker, returning a NEW set (never mutates the input).
 * "All industries" is a single exclusive choice; any individual industry clears
 * "all" first. Unchecking "All" yields an empty set (= nothing selected).
 */
export function toggleVertical(selected: Set<string>, vertical: string, checked: boolean): Set<string> {
  if (vertical === ALL_VERTICAL) {
    return checked ? new Set([ALL_VERTICAL]) : new Set()
  }
  const next = new Set(selected)
  next.delete(ALL_VERTICAL) // picking any single industry drops "all"
  if (checked) next.add(vertical)
  else next.delete(vertical)
  return next
}

/** The individual verticals to import (drops the "all" sentinel). Empty when
 *  the selection is "all" or nothing. */
export function selectedVerticals(selected: Set<string>): string[] {
  return [...selected].filter((v) => v !== ALL_VERTICAL)
}

/**
 * How many contacts the current selection resolves to — the whole pool when
 * "all" is picked, otherwise the summed counts of the chosen verticals (0 when
 * nothing is selected, which disables the Add button).
 */
export function selectedContactCount(
  selected: Set<string>,
  breakdown: ReadonlyArray<{ vertical: string; count: number }>,
  total: number,
): number {
  if (isAllVerticals(selected)) return total
  return breakdown.filter((b) => selected.has(b.vertical)).reduce((n, b) => n + b.count, 0)
}
